/**
 * 部署新版本后，旧标签页持有的 index.html 仍然引用已被删除的旧 hash chunk，
 * 懒加载这些 chunk 时会 404 触发 ChunkLoadError；新旧 bundle 混跑时也可能出现
 * useSyncExternalStore 等运行时不兼容错误。这里统一捕获这类错误并自动刷新到
 * 最新版本，同时用 sessionStorage 做防死循环保护。
 */

const RELOAD_FLAG_KEY = 'oc:chunk-reloaded-at'
// 同一窗口内多久之内不再重复刷新，避免在最新版本仍报错时无限刷新
const RELOAD_WINDOW_MS = 5_000

const CHUNK_ERROR_PATTERNS = [
  'Loading chunk',
  'Loading CSS chunk',
  'ChunkLoadError',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
  'Failed to load module script',
  'error loading module',
]

/** 新旧 bundle 混跑时常见的可恢复运行时错误 */
const DEPLOY_RECOVERABLE_ERROR_PATTERNS = [
  'getSnapshot is not a function',
  'getServerSnapshot is not a function',
  'subscribe is not a function',
  'Invalid hook call',
  "Cannot read properties of undefined (reading 'useSyncExternalStore')",
  "Unexpected token '<'",
]

const matchesAnyPattern = (message: string | null | undefined, patterns: string[]): boolean => {
  if (!message) return false
  return patterns.some(pattern => message.includes(pattern))
}

const isChunkLoadMessage = (message?: string | null): boolean => {
  return matchesAnyPattern(message, CHUNK_ERROR_PATTERNS)
}

const isDeployRecoverableMessage = (message?: string | null): boolean => {
  return (
    isChunkLoadMessage(message) ||
    matchesAnyPattern(message, DEPLOY_RECOVERABLE_ERROR_PATTERNS)
  )
}

const getErrorMessage = (error: unknown): string | null => {
  if (!error) return null
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message
    return typeof message === 'string' ? message : String(message)
  }
  return String(error)
}

/**
 * 资源加载失败的 url 是否指向本站的构建产物（避免第三方脚本/图片失败误触发刷新）
 */
const isOwnAssetUrl = (url?: string | null): boolean => {
  if (!url) return false
  try {
    const parsed = new URL(url, window.location.href)
    if (parsed.origin !== window.location.origin) return false
    return parsed.pathname.includes('/assets/')
  } catch {
    return false
  }
}

const reloadOnce = (): void => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_FLAG_KEY) || '0')
    if (Date.now() - last < RELOAD_WINDOW_MS) {
      // 刚刷新过仍然报错，说明刷新解决不了，停止刷新避免死循环
      return
    }
    window.sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()))
  } catch {
    // sessionStorage 不可用时直接刷新
  }
  window.location.reload()
}

/**
 * 给定任意错误判断是否为 chunk 加载失败，供 ErrorBoundary 复用。
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false
  if (typeof error === 'string') return isChunkLoadMessage(error)
  if (error instanceof Error) {
    return error.name === 'ChunkLoadError' || isChunkLoadMessage(error.message)
  }
  return isChunkLoadMessage(getErrorMessage(error))
}

/**
 * 给定任意错误判断是否为部署后可刷新恢复的错误（chunk 加载失败 + 运行时不兼容）。
 */
export const isDeployRecoverableError = (error: unknown): boolean => {
  if (!error) return false
  if (isChunkLoadError(error)) return true

  if (error instanceof Error) {
    return isDeployRecoverableMessage(error.message)
  }

  if (typeof error === 'string') {
    return isDeployRecoverableMessage(error)
  }

  return isDeployRecoverableMessage(getErrorMessage(error))
}

export const reloadForChunkError = reloadOnce
export const reloadForDeployError = reloadOnce

let installed = false

export function setupChunkErrorRecovery(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  // 捕获 <script> / <link> 等资源加载失败（不会冒泡，需在捕获阶段监听）
  window.addEventListener(
    'error',
    event => {
      const target = event.target as (HTMLElement & { src?: string; href?: string }) | null
      if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
        if (isOwnAssetUrl(target.src || target.href)) {
          reloadOnce()
        }
        return
      }
      if (isDeployRecoverableMessage(event.message)) {
        reloadOnce()
      }
    },
    true
  )

  // 捕获动态 import 失败或运行时不兼容抛出的 Promise rejection
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason
    const message = typeof reason === 'string' ? reason : (reason?.message ?? String(reason))
    if (isDeployRecoverableMessage(message)) {
      reloadOnce()
    }
  })
}
